import type { User, Withdrawal } from '@library/types/payments';
import {
  withdrawalMethodDisplay,
  withdrawalStatusDisplay
} from '@library/component-utils/paymentsfilters';
import { Button } from '@library/components/buttons';
import { DateTime } from 'luxon';
import { Modal } from '@library/components/modals/Modal';
import { StatusIcon } from '@library/components/StatusIcon';
import { WithdrawalsDetailHeader } from '@library/components/modals/WithdrawalsDetailModal/WithdrawalsDetailHeader';
import { convertCentsToDollars } from '@library/utils';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getMethodInfoLines } from '@bo-utils/payments';

interface WithdrawalsDetailModalProps {
  user: User;
  withdrawal: Withdrawal | undefined;
  onCancelModal: () => void;
}

interface FieldProps {
  classes?: string;
  title: string;
  subTitle: string;
  subTitle2?: string;
  icon?: JSX.Element | null;
}

const Field = ({
  classes = '',
  title,
  subTitle,
  subTitle2 = '',
  icon = null
}: FieldProps) => {
  return (
    <div className={`${classes}`}>
      <div className="text-sm">{title}</div>
      <div>
        {icon}
        <div className="inline-block text-xs">{subTitle}</div>
        {subTitle2 && <div className="text-xs">{subTitle2}</div>}
      </div>
    </div>
  );
};

export const WithdrawalsDetailModal = ({
  user,
  withdrawal,
  onCancelModal
}: WithdrawalsDetailModalProps) => {
  if (!withdrawal) {
    return null;
  }

  const {
    method,
    amount,
    card,
    bank,
    paypal,
    venmo,
    status,
    created,
    updated,
    merchantReferenceId,
    location,
    message
  } = withdrawal;

  const { stateAbbreviation, lat, long } = location || {
    stateAbbreviation: '?',
    lat: '?',
    long: '?'
  };

  const { methodLine1, methodLine2 } = getMethodInfoLines(
    method,
    withdrawalMethodDisplay(method),
    card,
    bank,
    paypal,
    venmo
  );

  const userName = `${user.id} ${user.first_name} ${user.last_name}`;
  const { email } = user;

  return (
    <Modal isOpen onCancelModal={onCancelModal} maxWidth="md">
      <div className="bg-soFresh-100 outline-hidden focus:outline-hidden relative w-full flex-col rounded-lg border border-solid border-gray-600 shadow-lg">
        <div className="border-soFresh-140 flex h-14 items-start justify-between rounded-t border-b border-solid">
          <WithdrawalsDetailHeader
            withdrawal={withdrawal}
            onCancelModal={onCancelModal}
          />
        </div>
        <div className="p-6">
          <div className="grid grid-cols-6 gap-4">
            <Field classes="col-span-2" title="USER" subTitle={userName} />
            <Field classes="col-span-4" title="EMAIL" subTitle={email} />
            <Field
              classes="col-span-2"
              title="STATUS"
              subTitle={withdrawalStatusDisplay(status)}
              icon={<StatusIcon status={status} />}
            />
            <Field
              classes="col-span-2"
              title="CREATED AT"
              subTitle={formatWithTimezoneChange(
                DateTime.fromISO(created),
                'LL/dd/yyyy t ZZZZ'
              )}
            />
            <Field
              classes="col-span-2"
              title="AMOUNT"
              subTitle={`${convertCentsToDollars(amount)}`}
            />
            <Field
              classes="col-span-3"
              title="PAYMENT ID"
              subTitle={merchantReferenceId}
            />
            <Field
              classes="col-span-2"
              title="PAYMENT METHOD"
              subTitle={methodLine1}
              subTitle2={methodLine2}
            />
            <Field classes="col-span-1" title="" subTitle="" />
            <Field
              classes="col-span-3"
              title="STATE / LOCATION"
              subTitle={`State: ${stateAbbreviation} Lat: ${lat} Long: ${long}`}
            />
            <Field classes="col-span-2" title="UPDATED AT" subTitle={updated} />
            <Field classes="col-span-1" title="" subTitle="" />
            <Field
              classes="col-span-full"
              title="ERROR"
              subTitle={message ?? 'None'}
            />
          </div>
        </div>
        <div className="ml-auto flex h-full items-end justify-end">
          <Button
            className="bg-stankonia-100 my-6 mr-6 flex justify-center rounded-lg p-3 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
            title="WithdrawalsDetailModalCloseButton"
            onClick={onCancelModal}
            type="button"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
